/**
 * English copy — the SHAPE every other locale must match. Keep keys semantic
 * (what the string is for), not positional. Anything a contributor reads
 * before trusting us with a story lives here; write it like a person.
 */
import { BRAND_NAME, BRAND_NAME_FA, BRAND_TAGLINE, CONTACT_EMAIL } from "../brand.js";

export const en = {
  meta: {
    siteName: BRAND_NAME,
    /** Fixed Persian title shown beside the Latin wordmark in BOTH locales. */
    siteNameFa: BRAND_NAME_FA,
    /** Shown beside the Latin name in English. Empty in FA, where the Persian name already says it. */
    brandTagline: BRAND_TAGLINE,
    tagline: "Stories that travel.",
    logline:
      "Qissakhana makes documentaries out of first-hand accounts of leaving. Our first film is My Child.",
    description:
      "Qissakhana, House of Stories, is a small documentary banner. Our first film, My Child, follows four Afghan LGBTQIA+ people into exile in Europe. Share your story privately, follow the work, and help the next film get made.",
    statusBadge: "First film · My Child",
    provenance: "First-hand accounts of leaving, told by the people who lived them.",
    pages: {
      home: "Qissakhana, House of Stories — documentaries, told first-hand",
      film: "My Child — a documentary by Kaniz Moradi",
      share: "Share your story — Qissakhana",
      support: "Support the films — Qissakhana",
      contact: "Get involved — Qissakhana",
      clips: "Clips — Qissakhana",
      privacy: "Privacy — Qissakhana",
      terms: "Submission terms — Qissakhana",
      withdraw: "Your submission — Qissakhana",
      notFound: "Page not found — Qissakhana",
    },
    descriptions: {
      share:
        "Tell us your story in writing, as a voice note, a video, or a link. Private by default, anonymous if you want, stored in Sweden.",
      support: "We're not taking money yet. Leave your email and be first to know when supporting the film becomes possible.",
      contact: "Want to be part of future productions, suggest a story, or reach the team? Write to us.",
      clips: "Watch and share clips from Qissakhana.",
      film: "My Child is a 22-minute documentary by Kaniz Moradi. Four Afghan LGBTQIA+ people tell the story of growing up, getting out, and what Europe turned out to be.",
    },
  },

  nav: {
    film: "The film",
    share: "Share your story",
    about: "About",
    clips: "Clips",
    support: "Support",
    contact: "Contact",
    language: "Language",
    switchTo: "فارسی",
    skipToContent: "Skip to content",
    menu: "Menu",
    close: "Close",
  },

  hero: {
    kicker: "Documentaries, told first-hand",
    title: "Every story here was carried a long way.",
    lede: "Qissakhana makes films out of what people carry when they leave. The first one, My Child, is four Afghan LGBTQIA+ people telling their own story in their own language. We are still listening for the next one. Yours could be part of it.",
    ctaShare: "Share your story",
    ctaFilm: "Watch the trailer",
    ctaUpdates: "Get updates",
    emailLabel: "Your email",
    emailPlaceholder: "you@example.com",
    emailButton: "Be first to see the trailer",
    emailHint: "One field, no name. We send a confirmation first; unsubscribe any time.",
    counter: "{count} stories shared so far",
  },

  about: {
    heading: "About Qissakhana",
    p1: "Qissakhana, Persian for a house of stories (\u2067قصه\u200cخانه\u2069), began with a simple question: what does a life look like once it has been split across two places? The answers are rarely simple. They are phone calls at odd hours, a tea glass that survived three moves, a language the children speak with an accent.",
    p2: "We make one film at a time, slowly, entirely out of first-hand accounts. The first is My Child: four Afghan LGBTQIA+ people who left Afghanistan and now live in Germany and Belgium, talking straight to camera for twenty-two minutes. The next film is not made yet. That is why we are still listening.",
    p3: "Everything you share stays private to our small team unless you decide otherwise. That is not fine print; it is the whole point.",
    directorNoteHeading: "A note from Kaniz",
    directorNote:
      "We are a small team making these films slowly and carefully. I read and listen to every story that arrives. We may not be able to reply to each one, but every one of them shapes what the film becomes.",
    howWeWorkHeading: "How we work",
    howWeWork:
      "Filming is the short part. Editing, translating, subtitling and mixing take far longer, and we sit with the material rather than rush it. While that happens we keep collecting stories, for the film in hand and for the one after it.",
    cta: "Share your story",
  },

  trailer: {
    heading: "Trailer",
    comingTitle: "The trailer is on its way",
    comingBody: "We'll tell you first.",
    play: "Play trailer",
    playClip: "Play clip",
    loadsFromYouTube: "Playing loads the video from YouTube.",
    warning: "Content warning: the trailer refers to sexual violence against a child and to suicide.",
  },

  /**
   * My Child — the first film under the banner. Facts here (runtime, language,
   * credits, the four contributors and their on-screen roles) are taken from the
   * delivered cut; see docs/film/my-child/scene-index.md.
   */
  film: {
    kicker: "Our first film",
    title: "My Child",
    byline: "A documentary by Kaniz Moradi",
    runtime: "22 minutes",
    runtimeLabel: "Runtime",
    languageLabel: "Language",
    language: "Dari, with English subtitles",
    directorLabel: "Director",
    director: "Kaniz Moradi",

    logline:
      "Four Afghan LGBTQIA+ people tell the story of growing up, getting out, and what Europe turned out to be.",

    /** Alt text per poster. They are three different images, so three descriptions. */
    posters: {
      portrait: "Poster: Najib Faizi sits in a yellow dress against a pale studio backdrop, one hand raised to her face.",
      object: "Poster: a single pale flower lit against near black.",
      dance: "Poster: Silk dances in a traditional Afghan dress of deep indigo and silver.",
    },

    synopsisHeading: "What it is",
    synopsis1:
      "Najib, Nora, Diba and Silk grew up in Afghanistan being told what they were. Each of them left. My Child is twenty-two minutes of the four of them talking straight to camera about the childhoods they survived, the journeys that got them out, and what they found when they arrived.",
    synopsis2:
      "It is not a rescue story. Europe is where they are protected by law and also where a neighbour spits at them, where a flat of six years still feels like a hotel rather than a home. The film ends anyway on what each of them is walking towards: a nursing degree, a beauty salon, a children's hospital, a wedding to sing at.",

    titleHeading: "Where the title comes from",
    titleOrigin:
      "A man on the refugee route offered a twelve-year-old shelter for the night and called him my child, which in Dari is close to the tenderest thing an adult can say to a child. What he did next is the reason this film exists. By the end of the twenty-two minutes the phrase belongs to the people it was taken from.",

    peopleHeading: "The people in the film",
    peopleNote:
      "All four are publicly out and say so on camera. They are credited here exactly as the film credits them.",

    warningHeading: "Before you watch",
    warning:
      "My Child contains first-hand accounts of child sexual abuse and rape, sexual assault by a teacher, violence from parents, and suicide in the Afghan LGBTQIA+ community. The people telling these stories chose to tell them. Decide for yourself whether now is the time to listen.",

    trailerHeading: "Trailer",
    trailerComing:
      "The trailer goes up shortly. Leave an email and it will reach you the day it does.",

    screeningsHeading: "Screenings and press",
    screenings:
      "For festival programming, press screeners or a community screening, write to us and say which.",
    screeningsCta: "Get in touch",

    backHeading: "This film is made of stories like yours",
    backBody:
      "My Child exists because four people decided to say it out loud. The next film needs the same.",
  },

  /** Contributor credits, exactly as the film's lower-thirds set them. */
  people: {
    najib: {
      name: "Najib Faizi",
      role: "Drag Queen (Germany)",
      note: "The first Afghan to come out as LGBTQIA+ on social media, and known in the community as the mother of LGBTQIA+. Now studying to become a nurse.",
    },
    nora: {
      name: "Nora Farash",
      role: "Transgender Woman (Germany)",
      note: "The first transgender woman to come out in the Afghan community.",
    },
    diba: {
      name: "Diba Mashriqi",
      role: "Transgender Woman (Germany)",
      note: "Works with LGBTQIA+ people in Germany while continuing her studies.",
    },
    silk: {
      name: "Silk",
      role: "Transgender Woman (Belgium)",
      note: "\u201cMany people haven\u2019t heard our voices. Now, with the sound of these bells in my hands, they will.\u201d",
    },
  },

  storyBlock: {
    heading: "Share your story",
    invitation: "Everyone has a story. Yours could shape the next film.",
    body: "You can write it, record it as a voice note, upload a short video, or send a link. You don't have to tell us who you are.",
    promptsHeading: "If you're not sure where to start",
    cta: "Start",
    private: "Private by default",
    anonymous: "Anonymous if you want",
    sweden: "Stored in Sweden",
  },

  share: {
    heading: "Share your story",
    stepOf: "Step {n} of 3",
    trustHeading: "What happens to your story",
    trust: {
      private:
        "Private by default. Your story goes only to our small team. It is never published or shown publicly without your separate, explicit permission.",
      sweden:
        "Stored in Sweden. Everything lives on our own server in the EU. No Google, no Meta, no analytics on this page.",
      identity:
        "You choose who you are. Real name, a pseudonym, or fully anonymous — and you can change that choice any time before anything is published.",
      withdraw:
        "You can withdraw. Every submission comes with a private link to withdraw it or change your preferences. No email or account needed.",
    },
    commitmentsHeading: "Our commitments",
    commitments: {
      contactFirst: "If we ever want to use your story publicly, we will contact you first, if you gave us a way to.",
      review: "Identifiable at-risk contributors can review their segment before the film is locked.",
      blur: "Face blurring and voice alteration are available.",
      redact: "We remove details that could identify family members before anything is published.",
      metadata: "Location and device metadata is stripped from every photo, video and voice note the moment it arrives.",
    },
    noAccount: "No sign-up. No account. Just your story.",
    readBy: "Read and listened to by Kaniz and the small Qissakhana team, not an inbox.",
    expectation: "We read and listen to everything. We may not be able to reply to every story.",

    step1Heading: "How would you like to tell it?",
    formats: {
      text: { title: "Write it", body: "Type your story. As long or as short as you like." },
      audio: { title: "Record a voice note", body: "Record here, or upload one you already have. Up to 10 minutes." },
      video: { title: "Record or upload a video", body: "Up to 300 MB and 15 minutes. Uploads can pause and resume." },
      link: { title: "Send a link", body: "Paste a link to something you have already posted." },
    },

    step2Heading: "Your story",
    promptsHint: "Tap a question to use it as a starting point, or ignore them entirely.",
    titleLabel: "Title (optional)",
    textLabel: "Your story",
    textPlaceholder: "Start anywhere. The middle is fine.",
    charCount: "{count} / {max}",
    noteLabel: "Anything you want to add (optional)",
    linkLabel: "Link",
    linkPlaceholder: "https://",
    linkWarning:
      "This link points to your own public account. Sharing it here does not make it private.",
    linkHelp: "YouTube, Instagram, TikTok, Telegram, a blog — anywhere public.",

    recorder: {
      start: "Start recording",
      stop: "Stop",
      recording: "Recording",
      rerecord: "Record again",
      use: "Use this recording",
      preview: "Listen back",
      elapsed: "Elapsed",
      remaining: "{time} left",
      permission: "Your browser will ask for permission to use the microphone.",
      unsupported: "Recording isn't available in this browser. You can upload a file instead.",
      orUpload: "Or upload an audio file",
      denied: "Microphone access was refused. You can still upload a file.",
    },
    uploader: {
      choose: "Choose a video",
      dropHint: "or drop it here",
      uploading: "Uploading",
      paused: "Paused",
      pause: "Pause",
      resume: "Resume",
      cancel: "Cancel",
      done: "Uploaded",
      processing: "Checking the file",
      progress: "{percent}% uploaded",
      tooLarge: "That file is over the {max} limit.",
      tooLong: "That recording is longer than {max}.",
      rejected: "We couldn't read that file. Try a different format or a shorter clip.",
      stuck: `This is taking longer than expected. You can keep waiting, or email the file to ${CONTACT_EMAIL}.`,
      failed: "The upload failed. Your text is saved on this page; try again when you're ready.",
    },

    step3Heading: "Who you are, and what you allow",
    identityLabel: "If your story is ever used publicly, credit me as",
    identity: {
      real_name: "My real name",
      pseudonym: "A pseudonym",
      anonymous: "Fully anonymous",
    },
    identityHint: "You can change this any time before anything is published.",
    nameLabel: "Name",
    pseudonymLabel: "Pseudonym",
    emailLabel: "Email (optional)",
    emailHint: "Only if you want us to be able to reach you.",
    emailRequiredHint: "Required for the contact options you ticked.",
    under18: `Under 18? Email us at ${CONTACT_EMAIL} — we'll need a parent or guardian's consent.`,
    consentHeading: "Your consent",
    consentIntro: "Two boxes are required. The rest are genuinely optional: your story is accepted without them.",
    required: "Required",
    optional: "Optional",
    readFull: "Read the full text",
    hideFull: "Hide the full text",
    submit: "Send my story",
    sending: "Sending",
    back: "Back",
    next: "Continue",

    done: {
      heading: "Thank you.",
      body: "Your story has arrived. It is now private to our small team.",
      linkHeading: "Save this link",
      linkBody:
        "It lets you withdraw your story or change how you're credited — even if you gave us no email. We can't recover it for you if it's lost.",
      copy: "Copy link",
      copied: "Copied",
      emailed: "We've also emailed it to you.",
      // Shown when the receipt genuinely failed to send, never merely when no
      // address was given. It has to say what to DO: this copy is the only one
      // left, and the token is stored hashed, so nobody can reissue it.
      notEmailed:
        "We couldn't send it to your email just now. The link above is the only copy, so please save it before you close this page.",
      expectation: "We read and listen to everything. We may not be able to reply to every story.",
      another: "Share another story",
    },
    errors: {
      generic: "Something went wrong. Your text is still here — please try again.",
      tooFast: "That was quick. Please take a moment and try again.",
      rateLimited: "Too many submissions from this connection. Please try again in an hour.",
      invalid: "Please check the highlighted fields.",
      storyRequired: "Please write at least a few sentences.",
      linkRequired: "Please paste a link that starts with https://",
      mediaRequired: "Please record or upload first.",
      nameRequired: "Please enter a name, or choose anonymous.",
      emailRequired: "Please add an email for the options you ticked.",
      emailInvalid: "That email address doesn't look right.",
      consentRequired: "The two required boxes must be ticked.",
    },
  },

  withdraw: {
    heading: "Your submission",
    body: "This private link controls one story you shared with Qissakhana.",
    statusLabel: "Status",
    identityHeading: "How you're credited",
    save: "Save",
    saved: "Saved",
    withdrawHeading: "Withdraw your story",
    withdrawBody:
      "Withdrawing deletes your story, any files you uploaded, and your details from our systems. This cannot be undone.",
    withdrawButton: "Withdraw and delete",
    confirm: "Yes, delete my story",
    cancel: "Keep it",
    withdrawn: "Your story has been deleted.",
    invalid: "This link isn't valid, or the story it pointed to has already been deleted.",
    usedNotice:
      `This story has been used in a production. We have stopped all new uses; published copies cannot be recalled. Your consent record is kept as required for the film. Email ${CONTACT_EMAIL} with any questions.`,
  },

  support: {
    heading: "Help us keep making these films",
    honest: "We're not taking money yet.",
    body: "A documentary costs real money: the edit, the sound mix, subtitles in two languages, licensing. When we open support, the people on this list hear first.",
    fundsHeading: "What support will fund",
    funds: {
      edit: "The final edit and colour",
      sound: "Sound mix",
      subtitles: "Subtitling in English and Persian",
      licensing: "Music and archive licensing",
    },
    formHeading: "Keep me posted",
    emailLabel: "Email",
    nameLabel: "Name (optional)",
    helpLabel: "How would you like to help?",
    help: {
      funding: "Contribute funding when it opens",
      screening: "Host a community screening",
      connect: "Connect us with someone who should see this",
      volunteer: "Volunteer skills — subtitling, translation, outreach, design",
    },
    amountLabel: "Roughly how much, if you'd contribute? (optional)",
    amounts: {
      lt25: "Under €25",
      "25_100": "€25 – €100",
      "100_500": "€100 – €500",
      gt500: "€500 or more",
      undisclosed: "I'd rather not say",
    },
    messageLabel: "Anything else? (optional)",
    consentContact: "Contact me when supporting the film becomes possible.",
    consentUpdates: "Also send me occasional updates about the film.",
    submit: "Keep me posted",
    done: "You're on the list. We've sent a confirmation email — click the button in it and you're set.",
    early: "You'll be the first to know when supporting becomes possible.",
  },

  contact: {
    heading: "Get involved",
    intro: "A small team reads everything. Replies can take a while.",
    topicLabel: "What is this about?",
    topics: {
      casting: "I'd like to act or appear in future productions",
      participation: "I want to suggest someone, or a story lead",
      press: "Press and screenings",
      other: "Something else",
    },
    nameLabel: "Name",
    emailLabel: "Email",
    phoneLabel: "Phone (optional)",
    languagesLabel: "Languages you're comfortable performing in",
    languages: { fa: "Persian", en: "English", sv: "Swedish", other: "Other" },
    languagesOtherLabel: "Which?",
    locationLabel: "Where are you based? (optional)",
    linksLabel: "Links — showreel, portfolio, social (optional, up to 3)",
    addLink: "Add another link",
    ageConfirm: "I am 18 or older.",
    messageLabel: "Your message",
    consentStore: "Qissakhana may store this message and contact me about it.",
    keepOnFile:
      "Keep my details on file for future casting calls. Deleted on request, and automatically after 24 months of inactivity.",
    submit: "Send",
    done: "Thank you. Your message has arrived; we'll be in touch when we can.",
  },

  clips: {
    heading: "Clips",
    empty: "Clips from our films will appear here as we release them.",
    emptyCta: "Get notified",
    watch: "Watch",
    more: "More clips",
    backToFilm: "This film is made of stories like yours.",
    shareCta: "Share your story",
  },

  shareBar: {
    heading: "Share this clip",
    captionLabel: "Caption",
    captionHint: "Edit it if you like — it's a starting point.",
    copyCaption: "Copy caption",
    captionCopied: "Caption copied. Paste it in {app}.",
    copyLink: "Copy link",
    linkCopied: "Link copied",
    native: "Share…",
    platforms: {
      telegram: "Telegram",
      whatsapp: "WhatsApp",
      instagram_copy: "Instagram",
      tiktok_copy: "TikTok",
      x: "X",
      facebook: "Facebook",
      bluesky: "Bluesky",
      linkedin: "LinkedIn",
      reddit: "Reddit",
    },
  },

  newsletter: {
    confirmHeading: "One more click",
    confirmBody: "Confirm that you want updates from Qissakhana.",
    confirmButton: "Yes, keep me posted",
    confirmed: "Confirmed. Thank you — we'll be in touch when there's news.",
    invalid: "This confirmation link isn't valid any more. You can sign up again on the homepage.",
    unsubscribeHeading: "Unsubscribe",
    unsubscribeButton: "Unsubscribe me",
    unsubscribed: "You're unsubscribed. Sorry to see you go.",
    checkInbox: "Check your inbox: we've sent you a confirmation email.",
  },

  footer: {
    dataNote: "Hosted in Sweden. Stories stay private.",
    privacy: "Privacy",
    terms: "Submission terms",
    contact: "Contact",
    email: CONTACT_EMAIL,
    rights: "© {year} Qissakhana",
  },

  common: {
    loading: "Loading",
    error: "Something went wrong.",
    retry: "Try again",
    required: "required",
    optional: "optional",
    yes: "Yes",
    no: "No",
    notFoundHeading: "Page not found",
    notFoundBody: "There's nothing here. The film is elsewhere.",
    home: "Back to the start",
    minutes: "minutes",
    mb: "MB",
  },

  admin: {
    title: "Qissakhana — review queue",
    login: "Sign in",
    password: "Password",
    signOut: "Sign out",
    queue: "Stories",
    contacts: "Messages",
    support: "Supporters",
    subscribers: "Subscribers",
    clips: "Clips",
    quarantine: "Quarantine",
    status: "Status",
    colStory: "Story",
    colIdentity: "Credited as",
    colConsent: "Consent",
    colReceived: "Received",
    colKind: "Format",
    colPrompt: "Prompt used",
    colEmail: "Email",
    statuses: {
      new: "New",
      reviewed: "Reviewed",
      shortlisted: "Shortlisted",
      contacted: "Contacted",
      used: "Used",
      not_used: "Not used",
    },
    anonymous: "Anonymous",
    pseudonym: "Pseudonym",
    realName: "Real name",
    consentFilm: "Film licence",
    consentWeb: "Web excerpts",
    consentContact: "May contact",
    granted: "granted",
    notGranted: "not granted",
    note: "Notes",
    delete: "Delete permanently",
    deleteConfirm: "Type DELETE to hard-delete this submission, its files and its details.",
    deleteRefusedUsed: "This story is marked as used in a production; its consent record must be kept. Use force only with a legal reason.",
    spamFlag: "Flagged by the honeypot — review before trusting",
    unflag: "Not spam",
    empty: "Nothing here yet.",
  },
} as const;
